// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Every call hits LOCAL SQLite via Tauri IPC (durable, offline, on-disk). Nothing
// here leaves the device — the AI season recap is called separately (brain.ts) with
// only the DERIVED-SIGNAL slice computed in Rust (signals.rs).
//
// Data model (Round Card — a multi-season scorecard archive + bag evolution):
//   round { date, course, holes (9|18), per-hole scores, total, putts,
//           fairways hit/eligible, GIR, conditions, partners, notes }
//   bag change { date, action(added|retired), club }

import { invoke } from "./tauri-web";

export interface Round {
  id: string;
  /** Calendar date the round was played (local tz), "YYYY-MM-DD". */
  played_on: string;
  course: string;
  /** 9 or 18. */
  holes_played: number;
  /** Per-hole strokes (length = holes_played). Empty when only a total was logged. */
  hole_scores: number[];
  /** Round total strokes (derived from hole_scores when present). */
  total_score: number;
  putts: number | null;
  fairways_hit: number | null;
  fairways_eligible: number | null;
  greens_in_reg: number | null;
  /** Course rating (e.g. 71.3) + slope (e.g. 128) for the tee played — used to estimate a handicap
   *  index. null when not recorded (the round is then skipped in the estimate). */
  course_rating: number | null;
  slope: number | null;
  conditions: string;
  partners: string[];
  notes: string;
  created_at: string;
  updated_at: string;
  /** Downscaled JPEG data-URL of a photo attached to this round (e.g. the scorecard), ON-DEVICE
   *  only. "" when unset. The model never sees it — a photo filed through chat is an attachment. */
  photo: string;
}

export interface NewRound {
  course: string;
  played_on?: string;
  holes_played?: number;
  hole_scores?: number[];
  total_score?: number;
  putts?: number | null;
  fairways_hit?: number | null;
  fairways_eligible?: number | null;
  greens_in_reg?: number | null;
  course_rating?: number | null;
  slope?: number | null;
  conditions?: string;
  partners?: string[];
  notes?: string;
}

export type RoundPatch = Partial<{
  course: string;
  played_on: string;
  holes_played: number;
  hole_scores: number[];
  total_score: number;
  putts: number | null;
  fairways_hit: number | null;
  fairways_eligible: number | null;
  greens_in_reg: number | null;
  course_rating: number | null;
  slope: number | null;
  conditions: string;
  partners: string[];
  notes: string;
}>;

export interface BagChange {
  id: string;
  changed_on: string;
  /** "added" | "retired". */
  action: string;
  club: string;
  notes: string;
  created_at: string;
}

export interface NewBagChange {
  action: string;
  club: string;
  changed_on?: string;
  notes?: string;
}

// ── Derived signals (computed on-device in Rust; only aggregates are sent to the brain) ──
// Mirrors src-tauri/src/signals.rs::RoundSignals. Every field is a computed aggregate —
// trends, rates, deltas, counts, course names, bag events — never a raw per-hole card or
// partner identity. This is the payload the AI recap reasons over (brain.ts), so the model
// can name SPECIFIC non-obvious patterns across a multi-season archive instead of re-reading
// every scorecard. Lower scores are better in golf.
export interface MonthPoint { month: string; avg_score_per_18: number; rounds: number }
export interface ScoringTrend {
  months: MonthPoint[];
  slope_per_month: number;
  direction: "improving" | "rising" | "flat";
  strokes_change: number;
}
export interface FormBlock {
  recent_avg_per_18: number;
  recent_rounds: number;
  prior_avg_per_18: number | null;
  prior_rounds: number;
  delta: number | null;
  direction: "lower" | "higher" | "flat" | "n/a";
}
export interface Accuracy {
  fairway_rate: number | null;
  fairway_rounds_tracked: number;
  fairway_rate_recent: number | null;
  fairway_rate_prior: number | null;
  fairway_direction: "climbing" | "slipping" | "flat" | "n/a";
  gir_per_round: number | null;
  gir_rounds_tracked: number;
}
export interface PuttingStat {
  avg_putts_per_18: number;
  rounds_tracked: number;
  recent_avg: number | null;
  prior_avg: number | null;
  direction: "fewer" | "more" | "flat" | "n/a";
}
export interface CourseStat {
  course: string;
  rounds: number;
  avg_score_per_18: number | null;
  best_score_per_18: number | null;
  last_played: string;
}
export interface ConditionStat { conditions: string; rounds: number; avg_score_per_18: number | null }
export interface PartnerStat { partner: string; rounds: number }
export interface BagEvent { changed_on: string; action: string; club: string }
export interface BagSummary {
  current_clubs: string[];
  current_count: number;
  recent_changes: BagEvent[];
  added_count: number;
  retired_count: number;
}
export interface RoundSignals {
  schema: string;
  generated_for: string;
  overall: {
    total_rounds: number;
    rounds_18: number;
    rounds_9: number;
    days_span: number;
    first_date: string | null;
    last_date: string | null;
    avg_score_per_18: number | null;
    best_score_per_18: number | null;
    note: string;
  };
  scoring_trend: ScoringTrend | null;
  form_block: FormBlock | null;
  accuracy: Accuracy;
  putting: PuttingStat | null;
  courses: CourseStat[];
  conditions: ConditionStat[];
  partners: PartnerStat[];
  bag: BagSummary;
}

export const store = {
  // Rounds
  listRounds: (opts?: { from?: string; to?: string; limit?: number }) =>
    invoke<Round[]>("list_rounds", { from: opts?.from, to: opts?.to, limit: opts?.limit ?? 50000 }),
  getRound: (id: string) => invoke<Round | null>("get_round", { id }),
  createRound: (input: NewRound) => invoke<Round>("create_round", { input }),
  updateRound: (id: string, patch: RoundPatch) => invoke<void>("update_round", { id, patch }),
  /** Attach a photo (downscaled JPEG data-URL) to a round — filed ON-DEVICE from chat (e.g. the
   *  scorecard). Never leaves the device, never sent to the model. "" clears it. */
  setRoundPhoto: (id: string, photo: string) => invoke<void>("set_round_photo", { id, photo }),
  deleteRound: (id: string) => invoke<void>("delete_round", { id }),
  // Bag-change log (the bag-evolution moat)
  listBagChanges: (limit = 5000) => invoke<BagChange[]>("list_bag_changes", { limit }),
  createBagChange: (input: NewBagChange) => invoke<BagChange>("create_bag_change", { input }),
  deleteBagChange: (id: string) => invoke<void>("delete_bag_change", { id }),
  /** Compute the derived-signals object on-device. `today` = client local YYYY-MM-DD so windows align to the user's clock. */
  computeSignals: (today: string) => invoke<RoundSignals>("compute_signals", { today }),
};

// ── Pure policy / display helpers ──────────────────────────────────────────────

/** Curated condition suggestions; the user can also type their own. */
export const DEFAULT_CONDITIONS = ["calm", "windy", "wet", "links", "hot", "cold", "cart", "walked"];
/** Curated club suggestions for the bag log; the user can type any club. */
export const COMMON_CLUBS = [
  "driver", "3-wood", "5-wood", "hybrid", "2-iron", "3-iron", "4-iron", "5-iron",
  "6-iron", "7-iron", "8-iron", "9-iron", "pitching wedge", "gap wedge", "sand wedge",
  "lob wedge", "52° wedge", "56° wedge", "60° wedge", "putter",
];

export function holesLabel(h: number): string {
  return h === 9 ? "9 holes" : "18 holes";
}

/** Total normalized to an 18-hole basis (a 9-hole round counts ×2). */
export function scorePer18(r: { holes_played: number; total_score: number }): number {
  return r.holes_played === 9 ? r.total_score * 2 : r.total_score;
}

/** Fairway-hit rate as a percent string, or "—" when not tracked. */
export function fairwayPct(hit: number | null, eligible: number | null): string {
  if (hit === null || eligible === null || eligible <= 0) return "—";
  return `${Math.round((hit / eligible) * 100)}%`;
}

const toLocalDate = (iso: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};
export const formatLocalDate = (d: Date): string =>
  `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
export const todayLocal = (): string => formatLocalDate(new Date());

/** Normalize a free-text tag — trimmed, collapsed whitespace, lowercased. */
export function normalizeText(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, " ");
}
/** Dedupe a list of names/clubs, preserving first-occurrence order, trimming blanks. */
export function dedupeTrim(raw: Iterable<string>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const r of raw) {
    const t = r.trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

// ── Recap-eligibility + at-a-glance summary copy (deterministic, on-device) ─────

export interface SeasonGlance {
  totalRounds: number;
  avgPer18: number | null;
  bestPer18: number | null;
  lastPlayed: string | null;
  index: number | null;
}
export function seasonGlance(rounds: Round[]): SeasonGlance {
  if (rounds.length === 0) return { totalRounds: 0, avgPer18: null, bestPer18: null, lastPlayed: null, index: null };
  const per18 = rounds.map(scorePer18);
  const avg = per18.reduce((a, b) => a + b, 0) / per18.length;
  const best = Math.min(...per18);
  // rounds come back sorted newest-first
  const last = rounds.reduce((acc, r) => (r.played_on > acc ? r.played_on : acc), rounds[0].played_on);
  // Est. index — the model has no course rating/slope, so a true WHS handicap isn't possible.
  // Approximated vs standard par (72/18), WHS-style: mean of the best ~40% of the last 20 rounds'
  // (18-normalised score − 72). A FACT about the player's own scoring, not advice.
  const recent = [...rounds].sort((a, b) => b.played_on.localeCompare(a.played_on)).slice(0, 20);
  let index: number | null = null;
  if (recent.length >= 3) {
    const diffs = recent.map((r) => scorePer18(r) - 72).sort((a, b) => a - b);
    const n = Math.max(1, Math.round(diffs.length * 0.4));
    index = Math.round((diffs.slice(0, n).reduce((a, b) => a + b, 0) / n) * 10) / 10;
  }
  return { totalRounds: rounds.length, avgPer18: Math.round(avg * 10) / 10, bestPer18: best, lastPlayed: last, index };
}

export function glanceCopy(g: SeasonGlance): string {
  if (g.totalRounds === 0) return "Log your first round — even one starts your scorecard archive.";
  if (g.totalRounds === 1) return "1 round logged · your archive has begun";
  if (g.totalRounds < 5) return `${g.totalRounds} rounds logged · keep the card going`;
  if (g.avgPer18 !== null) return `${g.totalRounds} rounds · averaging ${g.avgPer18} over 18`;
  return `${g.totalRounds} rounds logged`;
}

/** Pretty date for tiles + chips. */
export function fmtDate(s: string): string {
  if (!s) return "";
  return new Date(s.slice(0, 10) + "T00:00:00Z").toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric", year: "numeric", timeZone: "UTC",
  });
}
export function fmtShortDate(s: string): string {
  if (!s) return "";
  return new Date(s.slice(0, 10) + "T00:00:00Z").toLocaleDateString("en-US", {
    month: "short", day: "numeric", timeZone: "UTC",
  });
}

// ── Estimated handicap index (WHS-style, observational statistic — NOT an official handicap) ──
// Differential per qualifying 18-hole round = (113 / slope) × (adjusted_score − course_rating).
// Index = average of the best N differentials among the most recent 20 rounds, where N follows the
// WHS table (fewer rounds → fewer counted, with an adjustment), × 0.96. Rounds without a course
// rating + slope are skipped. Pure math over the user's own rounds; never advice.
export function estimateHandicap(rounds: Round[]): { index: number | null; used: number } {
  const diffs = rounds
    .filter((r) => r.holes_played === 18 && r.course_rating && r.slope && r.slope > 0 && r.total_score > 0)
    .sort((a, b) => b.played_on.localeCompare(a.played_on)) // most recent first
    .slice(0, 20)
    .map((r) => (113 / (r.slope as number)) * (r.total_score - (r.course_rating as number)));
  const n = diffs.length;
  if (n === 0) return { index: null, used: 0 };
  // WHS "best of" table (abbreviated): how many differentials count, and the adjustment.
  const table: Record<number, { count: number; adj: number }> = {
    3: { count: 1, adj: -2.0 }, 4: { count: 1, adj: -1.0 }, 5: { count: 1, adj: 0 },
    6: { count: 2, adj: -1.0 }, 7: { count: 2, adj: 0 }, 8: { count: 2, adj: 0 },
    9: { count: 3, adj: 0 }, 10: { count: 3, adj: 0 }, 11: { count: 3, adj: 0 },
    12: { count: 4, adj: 0 }, 13: { count: 4, adj: 0 }, 14: { count: 4, adj: 0 },
    15: { count: 5, adj: 0 }, 16: { count: 5, adj: 0 }, 17: { count: 6, adj: 0 },
    18: { count: 6, adj: 0 }, 19: { count: 7, adj: 0 }, 20: { count: 8, adj: 0 },
  };
  const rule = n >= 20 ? table[20] : n <= 3 ? table[3] : table[n];
  const best = [...diffs].sort((a, b) => a - b).slice(0, rule.count);
  const avg = best.reduce((s, x) => s + x, 0) / best.length;
  const index = Math.round((avg * 0.96 + rule.adj) * 10) / 10;
  return { index, used: n };
}
